import * as THREE from 'three';
import { isModelMesh } from '../viewport';

export type MaterialSlot = THREE.Material | THREE.Material[];

export const KEEP_NONE: ReadonlySet<THREE.Material> = new Set();

export function materialList(material: MaterialSlot): THREE.Material[] {
  return Array.isArray(material) ? material : [material];
}

// Meshes and lines carry the same geometry/material pair at runtime.
function asRenderable(object: THREE.Object3D): THREE.Mesh | null {
  const renderable = object as THREE.Mesh;
  return renderable.isMesh || (object as THREE.Line).isLine ? renderable : null;
}

export function disposeRenderable(object: THREE.Object3D): void {
  const renderable = asRenderable(object);
  if (!renderable) return;
  disposeMaterials(renderable.material);
  renderable.geometry.dispose();
}

// Gathered first because meshes of one model can share a material or a
// geometry, and a material's clones share its textures. `keep` holds the
// viewer's shared defaults, which the next model reuses. Textures need no such
// set: every parse builds its own.
export function disposeModel(
  object: THREE.Object3D,
  keep: ReadonlySet<THREE.Material>,
  originalMaterials: Map<string, MaterialSlot>,
): void {
  const materials = new Set<THREE.Material>();
  const geometries = new Set<THREE.BufferGeometry>();
  object.traverse((child) => {
    const original = originalMaterials.get(child.uuid);
    if (original) {
      materialList(original).forEach((material) => materials.add(material));
      originalMaterials.delete(child.uuid);
    }
    const renderable = asRenderable(child);
    if (!renderable) return;
    materialList(renderable.material).forEach((material) => materials.add(material));
    geometries.add(renderable.geometry);
  });
  const textures = new Set<THREE.Texture>();
  materials.forEach((material) => {
    if (keep.has(material)) return;
    materialTextures(material).forEach((texture) => textures.add(texture));
    material.dispose();
  });
  textures.forEach((texture) => texture.dispose());
  geometries.forEach((geometry) => geometry.dispose());
}

function materialTextures(material: THREE.Material): THREE.Texture[] {
  return Object.values(material).filter(
    (value): value is THREE.Texture => (value as THREE.Texture | null)?.isTexture === true,
  );
}

export function parsedMaterials(object: THREE.Object3D): Set<THREE.Material> {
  const materials = new Set<THREE.Material>();
  object.traverse((child) => {
    if (isModelMesh(child)) materialList(child.material).forEach((material) => materials.add(material));
  });
  return materials;
}

export function disposeMaterials(material: MaterialSlot): void {
  materialList(material).forEach((m) => m.dispose());
}

export function cloneOriginal(orig: MaterialSlot): MaterialSlot {
  return Array.isArray(orig) ? orig.map((m) => m.clone()) : orig.clone();
}

export function isSameMaterial(a: MaterialSlot, b: MaterialSlot): boolean {
  if (a === b) return true;
  return Array.isArray(a) && Array.isArray(b) && a.every((m, i) => m === b[i]);
}

export function saveOriginalMaterials(object: THREE.Object3D, target: Map<string, MaterialSlot>): void {
  object.traverse((child) => {
    if (!isModelMesh(child)) return;
    target.set(child.uuid, cloneOriginal(child.material));
  });
}
